import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  IconButton, 
  Divider, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  FormControlLabel, 
  Switch, 
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Home as HomeIcon, 
  ChevronRight, 
  Menu as MenuIcon, 
  Add, 
  Edit, 
  Delete, 
  Close,
  CheckCircle,
  Cancel,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncTechyShopMenu } from '../services/menuService';

function TechyShopMenu() {
  const navigate = useNavigate();
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  
  // Menu form state
  const [formData, setFormData] = useState({
    id: '',
    nome: '',
    label: '',
    posizione: '',
    link: '',
    enable: true,
    created_at: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni menu in tempo reale dal DB
  const menus = useLiveQuery(() => db.techyshop_menu.toArray(), []);

  const handleResetMenu = async () => {
    if (!window.confirm("Sei sicuro di voler resettare tutti i menu? Questa azione cancellerà i dati locali e li riscaricherà dal server.")) {
      return;
    }

    setIsResetting(true);
    try {
      const result = await syncTechyShopMenu(true);
      setToast({ 
        open: true, 
        message: result.success ? result.message : `Errore: ${result.message}`, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante il reset dei menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenModal = (menu = null) => {
    // Formattazione data per compatibilità datetime (YYYY-MM-DD HH:MM:SS)
    const now = new Date();
    const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

    if (menu) {
      setSelectedMenu(menu);
      setFormData({
        id: menu.id || '',
        nome: menu.nome || '',
        label: menu.label || '',
        posizione: menu.posizione || '',
        link: menu.link || '',
        enable: menu.enable === 1 || menu.enable === true,
        created_at: menu.created_at || mysqlTimestamp
      });
    } else {
      setSelectedMenu(null);
      setFormData({
        id: Date.now().toString(),
        nome: '',
        label: '',
        posizione: '',
        link: '',
        enable: true,
        created_at: mysqlTimestamp
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMenu(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveMenu = async () => {
    if (!formData.nome.trim()) {
      setToast({ open: true, message: 'Errore: Il nome è obbligatorio.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    
    // Formattazione data corrente per sicurezza
    const now = new Date();
    const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

    const menuData = {
      ...formData,
      enable: formData.enable ? 1 : 0,
      created_at: formData.created_at || mysqlTimestamp
    };

    try {
      if (selectedMenu) {
        // Logica di modifica con chiamata API editMenu
        const values = Object.entries(menuData)
          .filter(([key, value]) => key !== 'id' && value !== undefined && value !== null)
          .map(([key, value]) => ({ field: key, value }));

        if (values.length === 0) {
          handleCloseModal();
          setIsSaving(false);
          return;
        }

        const filters = [{ field: 'id', value: selectedMenu.id }];

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ values, filters }),
        });

        const result = await response.json();

        if (result.result) {
          await db.techyshop_menu.update(selectedMenu.id, menuData);
          setToast({ open: true, message: 'Menu aggiornato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante l'aggiornamento del menu sul server");
        }
      } else {
        // Logica di aggiunta con chiamata API addMenu
        const { id, ...rawPayload } = menuData;
        
        // Assicuriamoci che created_at sia nel formato corretto nel payload
        rawPayload.created_at = rawPayload.created_at || mysqlTimestamp;
        
        // Filtriamo i valori vuoti dal payload per l'invio al server
        const payload = Object.fromEntries(
          Object.entries(rawPayload).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        );
        
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (result.result) {
          // Utilizziamo l'ID restituito dal server se disponibile
          const newId = result.details?.id || result.details || Date.now();
          await db.techyshop_menu.add({ ...menuData, id: newId });
          setToast({ open: true, message: 'Menu creato e sincronizzato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante la creazione del menu sul server");
        }
      }
    } catch (error) {
      log("Errore nel salvataggio del menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMenu = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo menu?')) return;

    try {
      const filters = [{ field: 'id', value: id }];

      const response = await fetch('https://deadapi.techysumo.it/techyshop/delMenu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ filters }),
      });

      const result = await response.json();

      if (result.result) {
        await db.techyshop_menu.delete(id);
        setToast({ open: true, message: 'Menu eliminato con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione del menu sul server");
      }
    } catch (error) {
      log("Errore nell'eliminazione del menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs & Navigation */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs
          separator={<ChevronRight fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/techyshop')}
          >
            TechyShop
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Menu</Typography>
        </Breadcrumbs>

        <IconButton color="primary" onClick={() => navigate('/techyshop')} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Gestione <Box component="span" sx={{ color: 'primary.main' }}>Menu</Box>
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            In questa sezione potrai configurare i menu dello shop.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={isResetting ? <CircularProgress size={20} /> : <Refresh />} 
            onClick={handleResetMenu}
            disabled={isSaving || isResetting}
          >
            Reset Menu
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />} 
            onClick={() => handleOpenModal()}
            disabled={isSaving || isResetting}
          >
            Nuovo Menu
          </Button>
        </Box>
      </Box>

      {/* Menu Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Posizione</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Data Creazione</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {menus?.map((m) => (
              <TableRow 
                key={m.id} 
                hover 
                onClick={() => handleOpenModal(m)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{m.nome}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{m.posizione}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <Tooltip title={m.enable === 1 || m.enable === true ? "Attivo" : "Non Attivo"}>
                    {m.enable === 1 || m.enable === true ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(m); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteMenu(m.id); }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!menus || menus.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessun menu trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi/Modifica Menu */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {selectedMenu ? 'Modifica Menu' : 'Aggiungi Nuovo Menu'}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                disabled={isSaving}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Posizione"
                name="posizione"
                value={formData.posizione}
                onChange={handleInputChange}
                disabled={isSaving}
                placeholder="es: header, footer, sidebar"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                disabled={isSaving}
                placeholder="es: /products, https://google.com"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    name="enable" 
                    checked={formData.enable} 
                    onChange={handleInputChange} 
                    color="primary" 
                  />
                }
                label="Menu Abilitato"
                disabled={isSaving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isSaving}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveMenu} 
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 8 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
          Sistema integrato TechySumo • Ultimo aggiornamento: Marzo 2026
        </Typography>
      </Box>
    </Container>
  );
}

export default TechyShopMenu;
